//-- Copyright (c) 2021 TMT Insights Group, LLC
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { KeyboardEventHandler, useState } from 'react';
import { DateRangePicker, DateRangePickerProps, Range, StaticRange } from 'react-date-range';
import { addDays, endOfMonth, endOfWeek, endOfYear, format, isSameDay, parse, startOfMonth, startOfWeek, startOfYear } from 'date-fns';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material/styles';
import { Button, Grid, TextField } from '@mui/material';

import { useAppSelector } from '@/redux/hooks';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import PolarisIcon from '@/components/_common/PolarisIcon';

// https://github.com/hypeserver/react-date-range
// https://hypeserver.github.io/react-date-range/

const key = 'selection';

export type DateRangePickerValue = {
  startDate: Date | null;
  endDate: Date;
  key?: string;
};

export type DateRangepickerProps = {
  value?: DateRangePickerValue;
  months?: DateRangePickerProps['months'];
  autoApplyOnChange?: boolean;
  onChange: (val: DateRangePickerValue) => void;
};

const DateRangepicker = ({ value, months = 2, autoApplyOnChange = false, onChange }: DateRangepickerProps) => {
  const theme = useTheme();
  const userPreferences = useAppSelector(selectUserPreferences);
  const userPrefDateFormat = userPreferences.date_format ? userPreferences.date_format.replace('mm', 'MM') : 'MM/dd/yyyy';

  const [selectedRange, setSelectedRange] = useState<DateRangePickerValue[]>(
    value
      ? [value]
      : [
          {
            startDate: null,
            endDate: new Date(''),
            key
          }
        ]
  );

  const setDateStart = (date: string) => {
    setTextStartDate(date);
    const d = parse(date, userPrefDateFormat, new Date());

    d.setHours(0);
    d.setMinutes(0);
    d.setSeconds(0);
    if (!isNaN(d as any)) {
      setSelectedRange([{ ...selectedRange[0], startDate: d }]);
    }
  };

  const setDateEnd = (date: string) => {
    setTextEndDate(date);
    const d: Date = parse(date, userPrefDateFormat, new Date(date));
    d.setHours(23);
    d.setMinutes(59);
    d.setSeconds(59);
    if (!isNaN(d as any)) {
      const newRange = [{ ...selectedRange[0], endDate: d }];
      setSelectedRange(newRange);
    }
  };

  const onApply = () => {
    if (!selectedRange[0]?.startDate || !selectedRange[0]?.endDate) return;
    onChange(selectedRange[0]);
  };

  const [textStartDate, setTextStartDate] = useState<string>(
    value && value.startDate ? format(value.startDate, userPrefDateFormat, new Date() as any) : ''
  );
  const [textEndDate, setTextEndDate] = useState(value ? format(value.endDate as Date, userPrefDateFormat, new Date() as any) : '');

  const handleOnChange = (item: { selection: DateRangePickerValue }) => {
    const startDate = new Date(item.selection.startDate as Date);
    startDate.setHours(0);
    startDate.setMinutes(0);
    startDate.setSeconds(0);
    const endDate = new Date(item.selection.endDate);
    endDate.setHours(23);
    endDate.setMinutes(59);
    endDate.setSeconds(59);
    setSelectedRange([{ endDate, startDate, key: 'selection' }]);
    setTextStartDate(format(startDate, userPrefDateFormat, new Date() as any));
    setTextEndDate(format(endDate, userPrefDateFormat, new Date() as any));

    // auto apply changes without hitting apply button, for advance filters
    if (autoApplyOnChange && item.selection.startDate && item.selection.endDate && item.selection.startDate !== item.selection.endDate) {
      onChange({ endDate, startDate, key: 'selection' });
    }
  };

  const onKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Tab') e.stopPropagation();
  };

  const staticRanges = createStaticRanges(defaultStaticRanges as Range[]);

  return (
    <div>
      <StyledDateRangePicker
        onChange={handleOnChange as any}
        moveRangeOnFirstSelection={false}
        months={months}
        ranges={selectedRange as Range[]}
        staticRanges={staticRanges as StaticRange[]}
        inputRanges={[]}
        showDateDisplay={false}
        direction="horizontal"
        rangeColors={[theme?.palette?.primary?.main]}
        weekdayDisplayFormat="EEEEEE"
        monthDisplayFormat="MMMM yyyy"
        showMonthAndYearPickers={false}
      />
      <ActionBarGrid container alignItems="center" months={months}>
        <StyledTextInput
          variant="filled"
          color="primary"
          size="small"
          value={textStartDate}
          placeholder="Start Date"
          onChange={(e) => setDateStart(e.target.value)}
          autoComplete="off"
          onKeyDown={onKeyDown}
        />
        <ArrowPolarisIcon icon="right_arrow" />
        <StyledTextInput
          variant="filled"
          color="primary"
          size="small"
          value={textEndDate}
          placeholder="End Date"
          onChange={(e) => setDateEnd(e.target.value)}
          autoComplete="off"
          onKeyDown={onKeyDown}
        />
        {!autoApplyOnChange && (
          <Button variant="contained" color="primary" onClick={onApply} sx={{ mr: 2, ml: 'auto' }}>
            Apply
          </Button>
        )}
      </ActionBarGrid>
    </div>
  );
};

export default DateRangepicker;

const StyledDateRangePicker = styled(DateRangePicker)`
  margin-bottom: 50px;

  * {
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-weight: ${({ theme }) => theme.typography.fontWeightNormal};
  }

  .rdrCalendarWrapper,
  .rdrDefinedRangesWrapper,
  .rdrStaticRange {
    position: relative;
    color: ${({ theme }) => theme.palette.text.primary};
    border: 0;
    background: none;
    font-size: 0.88rem;
  }

  .rdrNextPrevButton,
  .rdrPprevButton {
    margin: 0 1.5rem;
    opacity: 0.7;
    background: ${({ theme }) => theme.palette.divider};

    &:hover {
      opacity: 1;
    }

    i {
      display: flex;
      align-items: center;
      justify-content: center;
      border: 0;

      &:after {
        height: 24px;
        width: 24px;
        margin-right: -5px;
        margin-top: 2px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  .rdrPprevButton i {
    &:after {
      content: url('https://api.iconify.design/ph/caret-left-bold.svg?width=14&height=14&color=${({ theme }) =>
        theme.palette.text.primary.replace('#', '%23')}');
    }
  }

  .rdrNextButton i {
    &:after {
      content: url('https://api.iconify.design/ph/caret-right-bold.svg?width=14&height=14&color=${({ theme }) =>
        theme.palette.text.primary.replace('#', '%23')}');
    }
  }

  .rdrDefinedRangesWrapper {
    border-right: 1px solid ${({ theme }) => theme.palette.divider};
    width: 130px;
  }

  .rdrSelected,
  .rdrInRange,
  .rdrStartEdge,
  .rdrEndEdge,
  .rdrDayNumber,
  .rdrDayStartPreview,
  .rdrDayInPreview,
  .rdrDayEndPreview {
    bottom: 0;
    border: 0;
    top: 0;
    right: 0;
    left: 0;
  }

  .rdrDayEndOfMonth .rdrInRange,
  .rdrDayEndOfMonth .rdrStartEdge,
  .rdrDayEndOfWeek .rdrInRange,
  .rdrDayEndOfWeek .rdrStartEdge {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .rdrDayStartOfMonth .rdrInRange,
  .rdrDayStartOfMonth .rdrEndEdge,
  .rdrDayStartOfWeek .rdrInRange,
  .rdrDayStartOfWeek .rdrEndEdge {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  .rdrDayStartPreview,
  .rdrDayInPreview,
  .rdrDayEndPreview {
    background: ${({ theme }) => theme.palette.action.hover};
  }

  .rdrDayStartPreview + span.rdrDayNumber,
  .rdrDayInPreview + span.rdrDayNumber,
  .rdrDayEndPreview + span.rdrDayNumber {
    z-index: 2;
  }

  .rdrMonth {
    width: 20rem;
    padding-right: 1rem;

    &:first-of-type {
      border-right: 1px solid ${({ theme }) => theme.palette.divider};
    }
  }

  .rdrMonthName {
    text-align: center;
    color: ${({ theme }) => theme.palette.text.primary};
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  }

  .rdrWeekDay {
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 0.85rem;
  }

  .rdrDay {
    height: 2.5rem;
    line-height: 2.5rem;
  }

  .rdrStartEdge,
  .rdrDayStartPreview {
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
  }

  .rdrEndEdge,
  .rdrDayEndPreview {
    border-top-right-radius: 50%;
    border-bottom-right-radius: 50%;
  }

  .rdrDay:not(.rdrDayPassive) {
    .rdrInRange {
      background: ${({ theme }) => theme.palette.primary.main}2a;

      ~ .rdrDayNumber span {
        color: ${({ theme }) => theme.palette.text.primary};
      }
    }

    .rdrInRange,
    .rdrStartEdge,
    .rdrEndEdge {
      ~ .rdrDayNumber span {
        font-weight: ${({ theme }) => theme.typography.fontWeightBold};
      }
    }
  }

  .rdrStaticRange:hover,
  .rdrStaticRange:focus {
    .rdrStaticRangeLabel {
      background: ${({ theme }) => theme.palette.divider};
    }
  }

  .rdrDayNumber {
    span {
      color: ${({ theme }) => theme.palette.text.primary};
    }
  }

  .rdrDayPassive span {
    opacity: 0.7;
  }

  .rdrDayDisabled {
    background: transparent;
    opacity: 0.5;
  }

  .rdrDayToday .rdrDayNumber {
    span {
      color: ${({ theme }) => theme.palette.primary.main};
      font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    }
    span:after {
      display: none;
    }
  }

  .rdrMonthAndYearWrapper {
    position: absolute;
    width: 100%;
    left: 0;
    top: 0;
    height: 24px;
  }

  .rdrMonthAndYearPickers {
    display: none;
  }
`;

const StyledTextInput = styled(TextField)`
  width: 8rem;

  input {
    padding: 0.65rem 1rem;
  }
`;

const ActionBarGrid = styled(Grid)<{ months?: number }>`
  width: ${({ months }) => (months === 1 ? '100%' : 'calc(100% - 129px)')};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  border-left: 1px solid ${({ theme }) => theme.palette.divider};
  position: absolute;
  bottom: 0;
  padding-left: 1rem;
  right: 0;
  height: 60px;

  .MuiButton-root {
    font-size: 0.9rem;
    line-height: 1.8rem;
  }
`;

const ArrowPolarisIcon = styled(PolarisIcon)`
  opacity: 0.5;
  margin-left: ${({ theme }) => theme.spacing(1)};
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

const defineds = {
  startOfWeek: startOfWeek(new Date()),
  endOfWeek: endOfWeek(new Date()),
  startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
  endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
  startOfMonth: startOfMonth(new Date()),
  endOfMonth: endOfMonth(new Date()),
  startOfYear: startOfYear(new Date()),
  endOfYear: endOfYear(new Date())
};

const defaultStaticRanges = [
  {
    label: 'Last week',
    range: function range() {
      return {
        startDate: defineds.startOfLastWeek,
        endDate: defineds.endOfLastWeek
      };
    }
  },
  {
    label: 'This week',
    range: function range() {
      return {
        startDate: defineds.startOfWeek,
        endDate: defineds.endOfWeek
      };
    }
  },
  {
    label: 'This Month',
    range: function range() {
      return {
        startDate: defineds.startOfMonth,
        endDate: defineds.endOfMonth
      };
    }
  },
  {
    label: 'This Year',
    range: function range() {
      return {
        startDate: defineds.startOfYear,
        endDate: defineds.endOfYear
      };
    }
  }
];

const staticRangeHandler = {
  range: {},
  isSelected(range: any) {
    const definedRange = (this as any).range();
    return isSameDay(range.startDate, definedRange.startDate) && isSameDay(range.endDate, definedRange.endDate);
  }
};

function createStaticRanges(ranges: Range[]) {
  return ranges.map((range) => ({ ...staticRangeHandler, ...range }));
}
